import Alert from 'developer-docs-mdx-components/components/Alert';
// @ts-expect-error No types given for file
import Markdown from 'developer-docs-shared-components/modules/Doc/components/Markdown';
import I18n from 'I18n';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UIFlex from 'UIComponents/layout/UIFlex';
import { NewVersionUnderDevelopment, PrivateBetaAvailable, PublicBeta } from 'developer-docs-mdx-components/utils/staticAlerts';
import PublicH1 from 'developer-docs-shared-components/components/PublicH1';
import ResponsiveRenderer from 'developer-docs-shared-components/components/ResponsiveRenderer';
import { keyToNavData } from 'developer-docs-shared-components/config/navData/keys';
import useLocale from 'developer-docs-shared-components/hooks/useLocale';
import { MOBILE_MENU_BREAKPOINT, mediaQueries } from 'developer-docs-shared-components/utils/mediaQueries';
import { useMemo } from 'react';
import styled from 'styled-components';
import UIDropdown from 'UIComponents/dropdown/UIDropdown';
import UIButtonWrapper from 'UIComponents/layout/UIButtonWrapper';
import UIList from 'UIComponents/list/UIList';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import { useGetBreadcrumbsByUrl } from '../../hooks/useGetBreadcrumbsByUrl';
import DocsBreadcrumbs from '../DocsBreadcrumbs';
import APIHeaderCard from './APIHeaderCard';
import { Paths } from '../RoutesPublic';
import { isUrlLocalized } from '../../utils/localization';
import { getEnglishBaseUrl } from '../../utils/urls';
import { LinkWithEnglishSuperset } from 'developer-docs-shared-components/components/Link';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const EndpointMetadata = styled.div.withConfig({
  displayName: "APIHeader__EndpointMetadata",
  componentId: "sc-1cge4e3-0"
})(["", "{margin-bottom:20px;}"], mediaQueries.aboveWidth(MOBILE_MENU_BREAKPOINT));
const H1 = styled(PublicH1).withConfig({
  displayName: "APIHeader__H1",
  componentId: "sc-1cge4e3-1"
})(["margin:0;", "{margin-top:7px;margin-bottom:0;}"], mediaQueries.aboveWidth(MOBILE_MENU_BREAKPOINT));
const APIHeader = ({
  apiKey,
  selectedVersion,
  setSwitchToc,
  versions
}) => {
  const title = keyToNavData['/reference/api'][apiKey].label() || I18n.text('developer-docs-ui.apiReference.fallbackTitle');
  const locale = useLocale();
  const breadcrumbs = useGetBreadcrumbsByUrl({
    title
  });
  const legacyIntroContent = useMemo(() => {
    return selectedVersion && selectedVersion.isLegacy && selectedVersion.introContent() || undefined;
  }, [selectedVersion]);
  const nonLegacyIntroContent = useMemo(() => {
    return selectedVersion && !selectedVersion.isLegacy && selectedVersion.introContent || undefined;
  }, [selectedVersion]);
  const publicApiSpecAlert = useMemo(() => {
    if (nonLegacyIntroContent) {
      switch (nonLegacyIntroContent.documentationBanner) {
        case 'NEW_VERSION':
          {
            return /*#__PURE__*/_jsx(NewVersionUnderDevelopment, {});
          }
        case 'PRIVATE_BETA':
          {
            return /*#__PURE__*/_jsx(PrivateBetaAvailable, {});
          }
        case 'PUBLIC_BETA':
          {
            return /*#__PURE__*/_jsx(PublicBeta, {});
          }
        default:
          {
            return undefined;
          }
      }
    }
    return undefined;
  }, [nonLegacyIntroContent]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(EndpointMetadata, {
      children: [!!breadcrumbs.length && /*#__PURE__*/_jsx(DocsBreadcrumbs, {
        breadcrumbs: breadcrumbs
      }), /*#__PURE__*/_jsxs(UIFlex, {
        className: "m-bottom-4",
        justify: "between",
        align: "center",
        children: [/*#__PURE__*/_jsx(H1, {
          children: title
        }), /*#__PURE__*/_jsxs(UIButtonWrapper, {
          children: [selectedVersion && /*#__PURE__*/_jsx(UITooltip, {
            disabled: versions.length > 1,
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "developer-docs-ui.apiReference.onlyOneVersion",
              options: {
                version: versions[0].versionName
              }
            }),
            children: /*#__PURE__*/_jsx(UIDropdown, {
              buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "developer-docs-ui.apiReference.selectedVersion",
                options: {
                  version: selectedVersion.versionName
                }
              }),
              buttonSize: "small",
              buttonUse: "tertiary-light",
              disabled: versions.length === 1,
              children: /*#__PURE__*/_jsx(UIList, {
                children: versions.map(version => {
                  const absolutePath = `${Paths.REFERENCE}/api${apiKey}/${version.versionName}`;
                  const isLocalized = isUrlLocalized(absolutePath, locale);
                  const isSelectedVersion = version.versionName === selectedVersion.versionName;
                  const sharedLinkProps = {
                    disabled: isSelectedVersion,
                    onClick: () => setSwitchToc(true),
                    children: version.versionName
                  };
                  return /*#__PURE__*/_jsx(UITooltip, {
                    disabled: !isSelectedVersion,
                    title: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "developer-docs-ui.apiReference.versionAlreadySelected",
                      options: {
                        version: version.versionName
                      }
                    }),
                    children: !isLocalized ?
                    /*#__PURE__*/
                    // External link pointing to en-us version
                    _jsx(LinkWithEnglishSuperset, Object.assign({
                      hrefLang: isSelectedVersion ? '' : 'en-us',
                      external: !isSelectedVersion,
                      href: getEnglishBaseUrl(absolutePath)
                    }, sharedLinkProps)) :
                    /*#__PURE__*/
                    // Router-aware link within the same localized site
                    _jsx(UIRouterLink, Object.assign({
                      to: absolutePath
                    }, sharedLinkProps))
                  }, version.versionName);
                })
              })
            })
          }), nonLegacyIntroContent && nonLegacyIntroContent.postmanCollectionId && /*#__PURE__*/_jsx(ResponsiveRenderer, {
            renderMobile: () => null,
            renderDesktop: () => /*#__PURE__*/_jsx(FormattedJSXMessage, {
              message: "developer-docs-ui.apiReference.runInPostmanLink_jsx",
              elements: {
                Button: UIButton
              },
              options: {
                use: 'tertiary',
                // TODO: For localization, should we use different locale-specific Postman URLs, if available?
                href: `https://app.getpostman.com/run-collection/${nonLegacyIntroContent.postmanCollectionId}`,
                external: true,
                size: 'small'
              }
            })
          })]
        })]
      })]
    }), publicApiSpecAlert || (legacyIntroContent && legacyIntroContent.alert ? /*#__PURE__*/_jsx(Alert, {
      type: legacyIntroContent.alertType,
      children: /*#__PURE__*/_jsx(Markdown, {
        children: legacyIntroContent.alert
      })
    }) : /*#__PURE__*/_jsx(_Fragment, {})), /*#__PURE__*/_jsx(APIHeaderCard, {
      legacyIntroContent: legacyIntroContent,
      nonLegacyIntroContent: nonLegacyIntroContent
    })]
  });
};
export default APIHeader;